import { AbiCoder, ZeroAddress, keccak256 } from "ethers";
import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { zeroAddress } from "viem";

import { getContract } from "config/contracts";
import { hashedPositionKey } from "config/dataStore";
import {
  PendingPositionUpdate,
  PositionDecreaseEvent,
  PositionIncreaseEvent,
  useSyntheticsEvents,
} from "context/SyntheticsEvents";
import { getAccountPositions, type AccountPosition } from "lib/apis/accounts";
import { useMulticall } from "lib/multicall";
import type { MulticallRequestConfig } from "lib/multicall/types";
import { getByKey } from "lib/objects";
import { FREQUENT_MULTICALL_REFRESH_INTERVAL } from "lib/timeConstants";
import { getPositionKey, parsePositionKey } from "sdk/utils/positions";

import { ContractMarketPrices, MarketsData, getContractMarketPrices } from "../markets";
import { TokensData } from "../tokens";
import { subscribeTradeDataRefresh } from "../tradeDataRefreshEvents";
import { filterRemovedPositionsData, useRemovedPositionKeys } from "./removedPositions";
import { Position, PositionsData } from "./types";

const MAX_PENDING_UPDATE_AGE = 600 * 1000; // 10 minutes
const POSITION_LIST_REQUEST_LIMIT = 1000;
const OPEN_POSITIONS_STORAGE_TTL = 30 * 60 * 1000; // 30 minutes
const EMPTY_POSITIONS_CONFIRMATION_GRACE_MS = 90 * 1000; // avoid clearing cache on transient empty reads
const DEFAULT_ABI_CODER = AbiCoder.defaultAbiCoder();
const ACCOUNT_POSITION_LIST_PREFIX = keccak256(DEFAULT_ABI_CODER.encode(["string"], ["ACCOUNT_POSITION_LIST"]));

type PositionsResult = {
  positionsData?: PositionsData;
  allPossiblePositionsKeys?: string[];
  error?: Error;
};

export function usePositions(
  chainId: number,
  p: {
    marketsData?: MarketsData;
    tokensData?: TokensData;
    account: string | null | undefined;
  }
): PositionsResult {
  const { marketsData, tokensData, account } = p;
  const { positionDecreaseEvents } = useSyntheticsEvents();
  const [disableBatching, setDisableBatching] = useState(true);
  const [lastStablePositionsData, setLastStablePositionsData] = useState<PositionsData | undefined>(undefined);
  const [persistedPositionsData, setPersistedPositionsData] = useState<PositionsData | undefined>(undefined);
  const [emptyPositionsObservedAt, setEmptyPositionsObservedAt] = useState<number | undefined>(undefined);
  const removedPositionKeys = useRemovedPositionKeys(chainId, account);
  const positionsStorageKey = useMemo(
    () => (account ? getOpenPositionsStorageKey(chainId, account) : undefined),
    [account, chainId]
  );

  const keysAndPrices = useKeysAndPricesParams({
    marketsData,
    tokensData,
    account,
  });

  const readerAddress = getContract(chainId, "SyntheticsReader");
  const positionListKey = account ? getAccountPositionListKey(account) : undefined;

  const { data: positionCountData, mutate: mutatePositionCount } = useMulticall(chainId, "usePositionCount", {
    key: account && readerAddress !== zeroAddress && positionListKey ? [account, positionListKey] : null,
    request: () => ({
      dataStore: {
        contractAddress: getContract(chainId, "DataStore"),
        abiId: "DataStore",
        calls: {
          positionCount: {
            methodName: "getBytes32Count",
            params: [positionListKey!],
          },
        },
      },
    }),
    parseResponse: (res) => {
      const rawCount = res.data.dataStore.positionCount.returnValues[0];
      return Number(rawCount ?? 0n);
    },
    refreshInterval: FREQUENT_MULTICALL_REFRESH_INTERVAL,
    clearUnusedKeys: true,
    keepPreviousData: true,
  });

  const positionCount = positionCountData ?? 0;
  const shouldFetchPositions = Boolean(
    account && readerAddress !== zeroAddress && positionCount > 0 && keysAndPrices.marketsKeys.length
  );
  const positionsRequestKey = shouldFetchPositions ? [account, keysAndPrices.marketsKeys, positionCount] : null;
  const positionRangeEnd = Math.min(positionCount, POSITION_LIST_REQUEST_LIMIT);

  const {
    data: positionsData,
    error: positionsError,
    isLoading,
    mutate: mutatePositionsData,
  } = useMulticall<MulticallRequestConfig<any>, PositionsData | undefined>(chainId, "usePositionsData", {
    key: positionsRequestKey,
    refreshInterval: FREQUENT_MULTICALL_REFRESH_INTERVAL,
    clearUnusedKeys: true,
    keepPreviousData: true,
    disableBatching,
    request: (): MulticallRequestConfig<any> =>
      shouldFetchPositions
        ? {
            reader: {
              contractAddress: readerAddress,
              abiId: "SyntheticsReader",
              calls: {
                positions: {
                  methodName: "getAccountPositionInfoList",
                  params: [
                    getContract(chainId, "DataStore"),
                    getContract(chainId, "ReferralStorage"),
                    account,
                    keysAndPrices.marketsKeys,
                    keysAndPrices.marketsPrices,
                    ZeroAddress,
                    0,
                    positionRangeEnd,
                  ],
                },
              },
            },
          }
        : {},
    parseResponse: (res) => {
      const positions = res.data.reader.positions.returnValues;

      return positions.reduce((positionsMap: PositionsData, positionInfo) => {
        const { position, fees, basePnlUsd } = positionInfo;
        const { addresses, numbers, flags, data } = position;
        const { account, market: marketAddress, collateralToken: collateralTokenAddress } = addresses;

        // Empty position
        if (numbers.increasedAtTime == 0n) {
          return positionsMap;
        }

        const positionKey = getPositionKey(account, marketAddress, collateralTokenAddress, flags.isLong);
        const contractPositionKey = hashedPositionKey(account, marketAddress, collateralTokenAddress, flags.isLong);

        positionsMap[positionKey] = {
          key: positionKey,
          contractKey: contractPositionKey,
          account,
          marketAddress,
          collateralTokenAddress,
          sizeInUsd: numbers.sizeInUsd,
          sizeInTokens: numbers.sizeInTokens,
          collateralAmount: numbers.collateralAmount,
          increasedAtTime: numbers.increasedAtTime,
          decreasedAtTime: numbers.decreasedAtTime,
          isLong: flags.isLong,
          pendingBorrowingFeesUsd: fees.borrowing.borrowingFeeUsd,
          fundingFeeAmount: fees.funding.fundingFeeAmount,
          claimableLongTokenAmount: fees.funding.claimableLongTokenAmount,
          claimableShortTokenAmount: fees.funding.claimableShortTokenAmount,
          pnl: basePnlUsd,
          positionFeeAmount: fees.positionFeeAmount,
          traderDiscountAmount: fees.referral.traderDiscountAmount,
          uiFeeAmount: fees.ui.uiFeeAmount,
          data,
        };

        return positionsMap;
      }, {} as PositionsData);
    },
  });

  useEffect(() => {
    if (positionsData && disableBatching) {
      setDisableBatching(false);
    }
  }, [disableBatching, positionsData]);

  const optimisticPositionsData = useOptimisticPositions({
    positionsData: positionsData,
    allPositionsKeys: keysAndPrices?.allPositionsKeys,
    isLoading,
  });

  const { data: backendPositionsResponse, mutate: mutateBackendPositions } = useSWR(
    account ? ["accountPositionsFallback", account, chainId] : null,
    () =>
      getAccountPositions(account!, {
        chainId,
        status: "OPEN",
        page: 1,
        pageSize: 500,
      }),
    {
      refreshInterval: FREQUENT_MULTICALL_REFRESH_INTERVAL,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateIfStale: true,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    return subscribeTradeDataRefresh(() => {
      void mutatePositionCount((currentData) => currentData, { revalidate: true });
      void mutatePositionsData((currentData) => currentData, { revalidate: true });
      void mutateBackendPositions();
    });
  }, [mutateBackendPositions, mutatePositionCount, mutatePositionsData]);

  const backendPositionsData = useMemo(() => {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const recentlyClosedPositionKeys = new Set(
      (positionDecreaseEvents || [])
        .filter(
          (event) =>
            event.sizeInUsd === 0n &&
            Number(event.decreasedAtTime) > 0 &&
            nowSeconds - Number(event.decreasedAtTime) <= MAX_PENDING_UPDATE_AGE / 1000
        )
        .map((event) => event.positionKey)
    );

    return buildBackendPositionsData(backendPositionsResponse?.items, account, recentlyClosedPositionKeys);
  }, [account, backendPositionsResponse?.items, positionDecreaseEvents]);

  const mergedPositionsData = useMemo(
    () => mergePositionsData(optimisticPositionsData, backendPositionsData),
    [backendPositionsData, optimisticPositionsData]
  );

  const hasPrimaryPositions = optimisticPositionsData && Object.keys(optimisticPositionsData).length > 0;
  const hasBackendPositions = backendPositionsData && Object.keys(backendPositionsData).length > 0;
  const isPrimarySourceUnavailable =
    Boolean(positionsError) ||
    positionCountData === undefined ||
    !shouldFetchPositions ||
    !positionsData ||
    Object.keys(positionsData).length === 0;
  const shouldSuppressPrimaryError =
    Boolean(account) && Boolean(hasBackendPositions) && !hasPrimaryPositions && isPrimarySourceUnavailable;

  const finalPositionsData = mergedPositionsData;
  const finalPositionsCount = finalPositionsData ? Object.keys(finalPositionsData).length : 0;
  const shouldObserveEmptyState =
    Boolean(account) &&
    positionCountData !== undefined &&
    !isLoading &&
    !positionsError &&
    positionCount === 0 &&
    finalPositionsCount === 0 &&
    !hasBackendPositions;

  const confirmedNoOpenPositions =
    Boolean(account) &&
    shouldObserveEmptyState &&
    emptyPositionsObservedAt !== undefined &&
    Date.now() - emptyPositionsObservedAt >= EMPTY_POSITIONS_CONFIRMATION_GRACE_MS;

  useEffect(() => {
    setLastStablePositionsData(undefined);
    setEmptyPositionsObservedAt(undefined);
  }, [account, chainId]);

  useEffect(() => {
    if (!account || !shouldObserveEmptyState) {
      setEmptyPositionsObservedAt(undefined);
      return;
    }

    setEmptyPositionsObservedAt((prev) => prev ?? Date.now());
  }, [account, shouldObserveEmptyState]);

  useEffect(() => {
    if (!positionsStorageKey || typeof window === "undefined") {
      setPersistedPositionsData(undefined);
      return;
    }

    try {
      const raw = window.localStorage.getItem(positionsStorageKey);
      const parsed = parsePersistedOpenPositions(raw);
      setPersistedPositionsData(parsed);
    } catch (_error) {
      setPersistedPositionsData(undefined);
    }
  }, [positionsStorageKey]);

  useEffect(() => {
    if (finalPositionsCount > 0 && finalPositionsData) {
      setLastStablePositionsData(finalPositionsData);
      return;
    }
    if (confirmedNoOpenPositions) {
      setLastStablePositionsData(undefined);
    }
  }, [confirmedNoOpenPositions, finalPositionsCount, finalPositionsData]);

  useEffect(() => {
    if (!positionsStorageKey || typeof window === "undefined") {
      return;
    }

    if (finalPositionsCount > 0 && finalPositionsData) {
      window.localStorage.setItem(positionsStorageKey, stringifyPersistedOpenPositions(finalPositionsData));
      return;
    }

    if (confirmedNoOpenPositions) {
      window.localStorage.removeItem(positionsStorageKey);
      setPersistedPositionsData(undefined);
    }
  }, [confirmedNoOpenPositions, finalPositionsCount, finalPositionsData, positionsStorageKey]);

  const positionsDataWithSticky =
    finalPositionsCount > 0
      ? finalPositionsData
      : confirmedNoOpenPositions
        ? {}
        : lastStablePositionsData ?? persistedPositionsData ?? finalPositionsData;
  const visiblePositionsData = filterRemovedPositionsData(positionsDataWithSticky, removedPositionKeys);

  return {
    positionsData: visiblePositionsData,
    error: shouldSuppressPrimaryError ? undefined : positionsError,
  };
}

function useKeysAndPricesParams(p: {
  account: string | null | undefined;
  marketsData: MarketsData | undefined;
  tokensData: TokensData | undefined;
}) {
  const { account, marketsData, tokensData } = p;

  return useMemo(() => {
    const values = {
      allPositionsKeys: [] as string[],
      marketsPrices: [] as ContractMarketPrices[],
      marketsKeys: [] as string[],
    };

    if (!account || !marketsData || !tokensData) {
      return values;
    }

    const markets = Object.values(marketsData);

    for (const market of markets) {
      const marketPrices = getContractMarketPrices(tokensData, market);

      if (!marketPrices || market.isSpotOnly) {
        continue;
      }

      values.marketsKeys.push(market.marketTokenAddress);
      values.marketsPrices.push(marketPrices);

      const collaterals = market.isSameCollaterals
        ? [market.longTokenAddress]
        : [market.longTokenAddress, market.shortTokenAddress];

      for (const collateralAddress of collaterals) {
        for (const isLong of [true, false]) {
          const positionKey = getPositionKey(account, market.marketTokenAddress, collateralAddress, isLong);
          values.allPositionsKeys.push(positionKey);
        }
      }
    }

    return values;
  }, [account, marketsData, tokensData]);
}

export function useOptimisticPositions(p: {
  positionsData: PositionsData | undefined;
  allPositionsKeys: string[] | undefined;
  isLoading: boolean;
}): PositionsData | undefined {
  const { positionsData, allPositionsKeys, isLoading } = p;
  const { positionDecreaseEvents, positionIncreaseEvents, pendingPositionsUpdates } = useSyntheticsEvents();

  return useMemo(() => {
    if (!allPositionsKeys || isLoading) {
      return undefined;
    }

    return allPositionsKeys.reduce((acc, key) => {
      const now = Date.now();

      const lastIncreaseEvent = positionIncreaseEvents
        ? positionIncreaseEvents.filter((e) => e.positionKey === key).pop()
        : undefined;
      const lastDecreaseEvent = positionDecreaseEvents
        ? positionDecreaseEvents.filter((e) => e.positionKey === key).pop()
        : undefined;

      const pendingUpdate =
        pendingPositionsUpdates?.[key] && (pendingPositionsUpdates[key]?.updatedAt ?? 0) + MAX_PENDING_UPDATE_AGE > now
          ? pendingPositionsUpdates[key]
          : undefined;

      let position: Position;

      if (getByKey(positionsData, key)) {
        position = { ...getByKey(positionsData, key)! };
      } else if (pendingUpdate && pendingUpdate.isIncrease) {
        position = getPendingMockPosition(pendingUpdate);
      } else {
        return acc;
      }

      if (
        lastIncreaseEvent &&
        lastIncreaseEvent.increasedAtTime > position.increasedAtTime &&
        lastIncreaseEvent.increasedAtTime > (lastDecreaseEvent?.decreasedAtTime || 0)
      ) {
        position = applyEventChanges(position, lastIncreaseEvent);
      } else if (
        lastDecreaseEvent &&
        lastDecreaseEvent.decreasedAtTime > position.decreasedAtTime &&
        lastDecreaseEvent.decreasedAtTime > (lastIncreaseEvent?.increasedAtTime || 0)
      ) {
        position = applyEventChanges(position, lastDecreaseEvent);
      }

      if (
        pendingUpdate &&
        ((pendingUpdate.isIncrease && pendingUpdate.updatedAtBlock > position.increasedAtTime) ||
          (!pendingUpdate.isIncrease && pendingUpdate.updatedAtBlock > position.decreasedAtTime))
      ) {
        position.pendingUpdate = pendingUpdate;
      }

      if (position.sizeInUsd > 0) {
        acc[key] = position;
      }

      return acc;
    }, {} as PositionsData);
  }, [
    allPositionsKeys,
    isLoading,
    pendingPositionsUpdates,
    positionDecreaseEvents,
    positionIncreaseEvents,
    positionsData,
  ]);
}

function applyEventChanges(position: Position, event: PositionIncreaseEvent | PositionDecreaseEvent) {
  const nextPosition = { ...position };

  nextPosition.sizeInUsd = event.sizeInUsd;
  nextPosition.sizeInTokens = event.sizeInTokens;
  nextPosition.collateralAmount = event.collateralAmount;
  nextPosition.pendingBorrowingFeesUsd = 0n;
  nextPosition.fundingFeeAmount = 0n;
  nextPosition.claimableLongTokenAmount = 0n;
  nextPosition.claimableShortTokenAmount = 0n;
  nextPosition.pendingUpdate = undefined;
  nextPosition.isOpening = false;

  // eslint-disable-next-line local-rules/no-logical-bigint
  if ((event as PositionIncreaseEvent).increasedAtTime) {
    nextPosition.increasedAtTime = (event as PositionIncreaseEvent).increasedAtTime;
  }

  // eslint-disable-next-line local-rules/no-logical-bigint
  if ((event as PositionDecreaseEvent).decreasedAtTime) {
    nextPosition.decreasedAtTime = (event as PositionDecreaseEvent).decreasedAtTime;
  }

  return nextPosition;
}

export function getPendingMockPosition(pendingUpdate: PendingPositionUpdate): Position {
  const { account, marketAddress, collateralAddress, isLong } = parsePositionKey(pendingUpdate.positionKey);

  return {
    key: pendingUpdate.positionKey,
    contractKey: hashedPositionKey(account, marketAddress, collateralAddress, isLong),
    account,
    marketAddress,
    collateralTokenAddress: collateralAddress,
    isLong,
    sizeInUsd: pendingUpdate.sizeDeltaUsd ?? 0n,
    collateralAmount: pendingUpdate.collateralDeltaAmount ?? 0n,
    sizeInTokens: pendingUpdate.sizeDeltaInTokens ?? 0n,
    increasedAtTime: pendingUpdate.updatedAtBlock,
    decreasedAtTime: 0n,
    pendingBorrowingFeesUsd: 0n,
    fundingFeeAmount: 0n,
    claimableLongTokenAmount: 0n,
    claimableShortTokenAmount: 0n,
    positionFeeAmount: 0n,
    uiFeeAmount: 0n,
    pnl: 0n,
    traderDiscountAmount: 0n,
    data: "0x",

    isOpening: true,
    pendingUpdate: pendingUpdate,
  };
}

function getAccountPositionListKey(account: string) {
  return keccak256(DEFAULT_ABI_CODER.encode(["bytes32", "address"], [ACCOUNT_POSITION_LIST_PREFIX, account]));
}

function toBigIntOrZero(value: unknown): bigint {
  if (value === null || value === undefined) return 0n;
  try {
    return BigInt(String(value));
  } catch (_error) {
    return 0n;
  }
}

type PersistedPosition = {
  key: string;
  contractKey: string;
  account: string;
  marketAddress: string;
  collateralTokenAddress: string;
  sizeInUsd: string;
  sizeInTokens: string;
  collateralAmount: string;
  pendingBorrowingFeesUsd: string;
  increasedAtTime: string;
  decreasedAtTime: string;
  isLong: boolean;
  fundingFeeAmount: string;
  claimableLongTokenAmount: string;
  claimableShortTokenAmount: string;
  isOpening?: boolean;
  pnl: string;
  positionFeeAmount: string;
  traderDiscountAmount: string;
  uiFeeAmount: string;
  data: string;
};

type PersistedOpenPositionsPayload = {
  updatedAt: number;
  positions: PersistedPosition[];
};

function getOpenPositionsStorageKey(chainId: number, account: string) {
  return JSON.stringify([chainId, account.toLowerCase(), "open-positions-cache-v1"]);
}

function stringifyPersistedOpenPositions(positionsData: PositionsData) {
  const payload: PersistedOpenPositionsPayload = {
    updatedAt: Date.now(),
    positions: Object.values(positionsData).map((position) => ({
      key: position.key,
      contractKey: position.contractKey,
      account: position.account,
      marketAddress: position.marketAddress,
      collateralTokenAddress: position.collateralTokenAddress,
      sizeInUsd: position.sizeInUsd.toString(),
      sizeInTokens: position.sizeInTokens.toString(),
      collateralAmount: position.collateralAmount.toString(),
      pendingBorrowingFeesUsd: position.pendingBorrowingFeesUsd.toString(),
      increasedAtTime: position.increasedAtTime.toString(),
      decreasedAtTime: position.decreasedAtTime.toString(),
      isLong: position.isLong,
      fundingFeeAmount: position.fundingFeeAmount.toString(),
      claimableLongTokenAmount: position.claimableLongTokenAmount.toString(),
      claimableShortTokenAmount: position.claimableShortTokenAmount.toString(),
      isOpening: position.isOpening,
      pnl: position.pnl.toString(),
      positionFeeAmount: position.positionFeeAmount.toString(),
      traderDiscountAmount: position.traderDiscountAmount.toString(),
      uiFeeAmount: position.uiFeeAmount.toString(),
      data: position.data,
    })),
  };

  return JSON.stringify(payload);
}

function parsePersistedOpenPositions(raw: string | null): PositionsData | undefined {
  if (!raw) {
    return undefined;
  }

  const parsed = JSON.parse(raw) as PersistedOpenPositionsPayload | undefined;

  if (!parsed?.updatedAt || !Array.isArray(parsed.positions)) {
    return undefined;
  }

  if (Date.now() - parsed.updatedAt > OPEN_POSITIONS_STORAGE_TTL) {
    return undefined;
  }

  return parsed.positions.reduce((acc, position) => {
    if (!position?.key || !position.account || !position.marketAddress || !position.collateralTokenAddress) {
      return acc;
    }

    acc[position.key] = {
      key: position.key,
      contractKey: position.contractKey,
      account: position.account,
      marketAddress: position.marketAddress,
      collateralTokenAddress: position.collateralTokenAddress,
      sizeInUsd: toBigIntOrZero(position.sizeInUsd),
      sizeInTokens: toBigIntOrZero(position.sizeInTokens),
      collateralAmount: toBigIntOrZero(position.collateralAmount),
      pendingBorrowingFeesUsd: toBigIntOrZero(position.pendingBorrowingFeesUsd),
      increasedAtTime: toBigIntOrZero(position.increasedAtTime),
      decreasedAtTime: toBigIntOrZero(position.decreasedAtTime),
      isLong: Boolean(position.isLong),
      fundingFeeAmount: toBigIntOrZero(position.fundingFeeAmount),
      claimableLongTokenAmount: toBigIntOrZero(position.claimableLongTokenAmount),
      claimableShortTokenAmount: toBigIntOrZero(position.claimableShortTokenAmount),
      isOpening: position.isOpening,
      pnl: toBigIntOrZero(position.pnl),
      positionFeeAmount: toBigIntOrZero(position.positionFeeAmount),
      traderDiscountAmount: toBigIntOrZero(position.traderDiscountAmount),
      uiFeeAmount: toBigIntOrZero(position.uiFeeAmount),
      data: position.data ?? "0x",
    };

    return acc;
  }, {} as PositionsData);
}

function convertBackendPositionToSdkPosition(row: AccountPosition): Position | undefined {
  const account = row.account?.toLowerCase();
  const marketAddress = row.market?.toLowerCase();
  const collateralTokenAddress = row.collateralToken?.toLowerCase();

  if (!account || !marketAddress || !collateralTokenAddress) {
    return undefined;
  }

  const isLong = Boolean(row.isLong);
  const key = getPositionKey(account, marketAddress, collateralTokenAddress, isLong);
  const nowMs = Date.now();
  const lastSeenAtMs = row.lastSeenAt ? new Date(row.lastSeenAt).getTime() : NaN;

  const contractKey =
    typeof row.positionKey === "string" && /^0x[0-9a-fA-F]{64}$/.test(row.positionKey)
      ? row.positionKey
      : hashedPositionKey(account, marketAddress, collateralTokenAddress, isLong);

  const timeSeconds = Number.isFinite(lastSeenAtMs) ? Math.floor(lastSeenAtMs / 1000) : Math.floor(nowMs / 1000);

  return {
    key,
    contractKey,
    account,
    marketAddress,
    collateralTokenAddress,
    sizeInUsd: toBigIntOrZero(row.sizeInUsd),
    sizeInTokens: toBigIntOrZero(row.sizeInTokens),
    collateralAmount: toBigIntOrZero(row.collateralAmount),
    pendingBorrowingFeesUsd: 0n,
    increasedAtTime: BigInt(timeSeconds),
    decreasedAtTime: 0n,
    isLong,
    fundingFeeAmount: 0n,
    claimableLongTokenAmount: 0n,
    claimableShortTokenAmount: 0n,
    pnl: 0n,
    positionFeeAmount: 0n,
    traderDiscountAmount: 0n,
    uiFeeAmount: 0n,
    data: "0x",
  };
}

export function buildBackendPositionsData(
  rows: AccountPosition[] | undefined,
  account: string | null | undefined,
  recentlyClosedPositionKeys: Set<string>
): PositionsData {
  const normalizedAccount = account?.toLowerCase();

  return (rows ?? []).reduce((acc, row) => {
    const rowAccount = row.account?.toLowerCase();
    if (!normalizedAccount || !rowAccount || rowAccount !== normalizedAccount) {
      return acc;
    }

    const position = convertBackendPositionToSdkPosition(row);
    if (!position) return acc;
    if (recentlyClosedPositionKeys.has(position.key)) return acc;
    acc[position.key] = position;
    return acc;
  }, {} as PositionsData);
}

export function mergePositionsData(
  optimisticPositionsData: PositionsData | undefined,
  backendPositionsData: PositionsData | undefined
): PositionsData | undefined {
  if (!optimisticPositionsData && !backendPositionsData) {
    return undefined;
  }

  const merged: PositionsData = {};
  const keyByContractKey = new Map<string, string>();

  const upsert = (position: Position) => {
    const contractKey = position.contractKey || position.key;
    const existingKey = keyByContractKey.get(contractKey);

    if (existingKey && existingKey !== position.key) {
      delete merged[existingKey];
    }

    merged[position.key] = position;
    keyByContractKey.set(contractKey, position.key);
  };

  if (backendPositionsData) {
    Object.values(backendPositionsData).forEach(upsert);
  }

  if (optimisticPositionsData) {
    Object.values(optimisticPositionsData).forEach(upsert);
  }

  return Object.keys(merged).length > 0 ? merged : undefined;
}
